import type * as THREE from 'three'
import {
  ImportedModelObjectMetadataService,
  type ImportedModelObjectMetadata,
} from '../metadata'
import { resolveImportedModelTopologyRepository } from '../topology/ImportedModelTopologyRepository'

/**
 * imported-model 可见 Mesh 身份解析器。
 *
 * 设计说明：
 * 1. hover、选择、碰撞都需要回答“这个 Mesh 是否是真实导入几何”；
 * 2. 该判断只消费 `metadata.importedModel` 与 topology repository，不再回退读取 legacy userData 语义字段；
 * 3. topology repository 表示运行时已经登记过的可见 body，metadata hierarchy 表示加载阶段声明的语义几何。
 */
export class ImportedModelMeshIdentityResolver {
  /**
   * 判断 Mesh 是否属于 imported-model 的真实可见几何。
   *
   * @param mesh 候选 Mesh。
   * @returns 是否可作为 imported-model 的第一阶段几何候选。
   */
  public static isImportedVisibleMesh(mesh: THREE.Mesh): boolean {
    const ownMetadata = ImportedModelObjectMetadataService.peekOwnMetadata(mesh)
    const importedScopeMetadata = ImportedModelObjectMetadataService.peekMetadataFromSelfOrAncestors(mesh)
    if (!importedScopeMetadata) {
      return false
    }

    if (ImportedModelMeshIdentityResolver.isImportedRuntimeWrapperScope(importedScopeMetadata)) {
      return false
    }

    if (ImportedModelMeshIdentityResolver.hasImportedTopologyBodyRecord(mesh)) {
      return true
    }

    if (ImportedModelMeshIdentityResolver.isExplicitlyNonSelectableImportedScope(importedScopeMetadata)) {
      return false
    }

    if (ownMetadata?.hierarchy?.meta?.hasDirectGeometry === true) {
      return true
    }

    const geometryScopeMetadata = ImportedModelMeshIdentityResolver.resolveGeometryScopeMetadata(mesh)
    if (
      geometryScopeMetadata
      && !ImportedModelMeshIdentityResolver.isImportedRuntimeWrapperScope(geometryScopeMetadata)
      && !ImportedModelMeshIdentityResolver.isExplicitlyNonSelectableImportedScope(geometryScopeMetadata)
    ) {
      return true
    }

    return false
  }

  /**
   * 判断 Mesh 是否已经注册为导入 topology 仓库的 visible body。
   *
   * @param mesh 候选 Mesh。
   * @returns 是否存在 body 拓扑记录。
   */
  private static hasImportedTopologyBodyRecord(mesh: THREE.Mesh): boolean {
    const repository = resolveImportedModelTopologyRepository(mesh)
    return (repository?.resolveBodyRecordsByVisibleMesh(mesh).length ?? 0) > 0
  }

  /**
   * 从自身或父链找到声明导入几何的 metadata。
   *
   * 这里允许“父级语义节点持有 metadata、子 Mesh 只承载几何”的装配场景，
   * 但不会把只有 fileId / selectionTarget 的 metadata 当作几何声明。
   *
   * @param mesh 候选 Mesh。
   * @returns 几何语义 metadata；不存在时返回 `null`。
   */
  private static resolveGeometryScopeMetadata(mesh: THREE.Mesh): ImportedModelObjectMetadata | null {
    let current: THREE.Object3D | null = mesh
    while (current) {
      const metadata = ImportedModelObjectMetadataService.peekMetadata(current)
      const hierarchyMeta = metadata?.hierarchy?.meta
      if (hierarchyMeta?.hasGeometry === true || hierarchyMeta?.hasDirectGeometry === true) {
        return metadata ?? null
      }
      current = current.parent
    }
    return null
  }

  /**
   * 判断 imported metadata 是否表示不应进入可选几何候选。
   *
   * @param metadata imported metadata。
   * @returns 是否不应参与候选。
   */
  private static isImportedRuntimeWrapperScope(metadata: ImportedModelObjectMetadata): boolean {
    return metadata.runtimeWrapper === true
      || metadata.hierarchy?.meta?.runtimeWrapper === true
  }

  /**
   * 判断 imported metadata 是否显式声明当前层级不可选。
   *
   * 设计说明：
   * `.puppy` 特征实体重新导入时，包根 Group 可因无直接几何而 selectable=false，
   * 但其子级 visible mesh 已经在 topology repository 中登记为实体 body；因此
   * “显式不可选”只能约束普通 GLB 几何路径，不能覆盖 body topology 的权威记录。
   *
   * @param metadata imported metadata。
   * @returns 是否显式声明不可选。
   */
  private static isExplicitlyNonSelectableImportedScope(metadata: ImportedModelObjectMetadata): boolean {
    return metadata.selectable === false
      || metadata.hierarchy?.selectable === false
  }
}
